import React from 'react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { GridRowProps } from '@progress/kendo-react-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Grid, Paper, Typography } from '@mui/material';
import { SiTrackPage } from '../../components/si-track-page';
import { SelectResult } from '../../components/select/select-chain.component';

import { useLoadData } from '../../hooks/useLoadData';
import { useSaveData } from '../../hooks/useSaveData';
import { ApiResponse } from '../../models/api-response';
import { useReactHookForm } from '../../hooks/useReactHookForm';

import { ConfirmDialogComponent } from './confirm-dialog.component';
import { UserPermissionName } from '../../models/user-permission-name';
import { DUXInspectionFilters } from './dux-inspection-request.filters';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { ScheduleDialog } from '../workorders/workorders-tab/shedule.dialog';
import { duxInspectionRequestColumns } from './dux-inspection-request-columns';

import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { workorderReportApiKeyStore } from '../../api/workorder-report-api-key-store';
import { ReadyDialogComponent } from '../workorders/workorders-tab/ready-dialog.component';
import { ReasonDialogComponent } from './reason-dialog.compoment';
import { ReadyConfirmationDialog } from './ready-confirmation-dialog.component';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { DuxInspectionRequest } from '../../models/dux-inspection-request';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
  actionButtonStyle: { padding: 13, backgroundColor: '#0000b5', color: 'white' }
};

interface CustomGridCellProps {
  field: string;
}

interface CellProps {
  workorder: any;
}

const getInspectorWithFilters = (filter: any) => {
  const { customerID, officeID, projectID, unitID, workOrderID, serviceID, wOStatusID, duxInspectionRequestType, requestInspection } = filter;

  let queryString = '';

  if (customerID) {
    queryString += `${queryString ? '&' : ''}customerID=${customerID}`;
  }
  if (officeID) {
    queryString += `${queryString ? '&' : ''}officeID=${officeID}`;
  }
  if (projectID) {
    queryString += `${queryString ? '&' : ''}projectID=${projectID}`;
  }
  if (unitID) {
    queryString += `${queryString ? '&' : ''}unitID=${unitID}`;
  }
  if (serviceID) {
    queryString += `${queryString ? '&' : ''}service=${serviceID}`;
  }
  if (wOStatusID) {
    queryString += `${queryString ? '&' : ''}workOrderStatusID=${wOStatusID}`;
  }
  if (workOrderID) {
    queryString += `${queryString ? '&' : ''}workOrderID=${workOrderID}`;
  }
  // if (duxInspectionRequestType) {
  //   queryString += `${queryString ? '&' : ''}inspectionID=${duxInspectionRequestType}`;
  // }
  if (requestInspection) {
    queryString += `${queryString ? '&' : ''}requestInspection=${requestInspection}`;
  }
  return queryString;
};

const getFiltesFromUrl = (params: URLSearchParams) => {
  return {
    customerID: params.get('customerID'),
    officeID: params.get('officeID'),
    projectID: params.get('projectID'),
    unitID: params.get('unitID'),
  };
};

export const DUXInspectionRequestPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDeclinedOpen, setIsDeclinedOpen] = useState<boolean>(false);
  const [scheduledDate, setScheduledDate] = useState<any>();
  const [openSchedule, setOpenSchedule] = useState<boolean>(false);
  const [reason, setReason] = useState<string>('');
  const [isOpenReason, setIsOpenReason] = useState<boolean>(false);
  const [workOrderData, setWorkOrderData] = useState<any>();
  const [selectedFilters, setSelectedFilters] = useState<SelectResult>(getFiltesFromUrl(searchParams));
  const [isOpenReadyDialog, setOpenReadyDialog] = useState(false);
  const [isConfirmReadyDialog, setConfirmReadyDialog] = useState(false);

  const form = useReactHookForm({
    defaultValues: {
      customer: { customerID: '', customerName: '' },
      office: { officeID: '', officeName: '' },
      project: { projectID: '', projectName: '' },
      unit: { unitID: '', unitName: '' },
      user: { userID: '', fullName: '' },
      service: { serviceID: '', serviceName: '' },
      dateCriteria: { dateCriteriaValue: '', dateCriteria: '' },
      // workOrderStatus: { workOrderStatusID: '', workOrderStatus: '' },
      action: '',
    },
  });

  const { isLoading: isStatusLoading, mutateAsync } = useSaveData(workOrdersApiKeyStore.updateWorkOrderStatus());

  const { isLoading: isDeclinedLoading, mutateAsync: mutateDeclinedAsync } =
    useSaveData(workOrdersApiKeyStore.declinedWorkOrder());

  const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(
    workorderReportApiKeyStore.getDuxInspectionRequest(selectedFilters),
    !!selectedFilters?.customerID ||
    !!selectedFilters?.officeID ||
    !!selectedFilters?.projectID ||
    !!selectedFilters?.unitID ||
    !!selectedFilters?.serviceID ||
    !!selectedFilters?.inspectorID ||
    !!selectedFilters?.wOStatusID ||
    !!selectedFilters?.dateFrom ||
    !!selectedFilters?.dateTo ||
    !!selectedFilters?.dateCriteria ||
    !!selectedFilters?.duxInspectionRequestType
  );

  const handleSubmit = (filtersData: any) => {
    const filteringData = {
      showAll: filtersData?.action ?? '',
      customerID: filtersData?.customer?.customerID ?? 0,
      officeID: filtersData?.office?.officeID ?? 0,
      projectID: filtersData?.project?.projectID ?? 0,
      unitID: filtersData?.unit?.unitID ?? 0,
      serviceID: filtersData?.service?.serviceID ?? 0,
      inspectorID: filtersData?.user?.userID ?? 0,
      // wOStatusID: filtersData?.workOrderStatus?.workOrderStatusID ?? 0,
      duxInspectionRequestType: filtersData?.inspectionType?.inspectionRequestTypeID ?? 0,
      dateFrom: filtersData?.fromDate ?? '',
      dateTo: filtersData?.toDate ?? '',
      dateCriteria: filtersData?.dateCriteria?.dateCriteriaValue ?? '',
    };
    setSelectedFilters(filteringData);
    refetch();
  };

  const handleShowAll = () => {
    form.handleSubmit(handleSubmit)();
  };

  const updateStatus = async (scheduleDate: any) => {
    const formatDate = (date: any) => {
      if (!date) return ''; // Handle null or undefined dates
      return dayjs(date).format('YYYY-MM-DD'); // Format as YYYY-MM-DD
    };
    const data = {
      workOrderID: workOrderData?.workOrderID,
      status: workOrderData?.actionType,
      proposedScheduledDate: formatDate(scheduleDate),
    };
    await mutateAsync({ data: data });
    refetch();
    setIsOpen(false);
    setScheduledDate('');
    setOpenReadyDialog(false);
    setWorkOrderData({});
  };

  const handleStatusClick = async () => {
    updateStatus(scheduledDate);
  };

  const handleSaveReasonClick = (reason: string) => {
    setReason(reason);
    setIsDeclinedOpen(true);
  };

  const handleWorkOrderDecline = async () => {
    const data = {
      workOrderID: workOrderData?.workOrderID,
      declineMessage: reason,
    };
    await mutateDeclinedAsync({ data: data });
    refetch();
    setIsOpenReason(false);
    setIsDeclinedOpen(false);
    setWorkOrderData({});
  };

  const schedulingRedirect = (workOrderID: any) => {
    selectedFilters.workOrderID = workOrderID;
    selectedFilters.requestInspection = true;
    navigate(`/workorders?${getInspectorWithFilters(selectedFilters)}`);
  };

  const WorkOrderCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <td style={{ cursor: 'pointer' }} onClick={() => schedulingRedirect(workorder?.workOrderID)}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.workOrderNumber}
        </Typography>
      </td>
    );
  };

  const InvoiceAmountCell = ({ workorder }: CellProps) => {
    const invoiceAmount = workorder?.invoiceAmountNumeric;

    const formattedAmount =
      invoiceAmount !== undefined && invoiceAmount !== null
        ? `$${parseFloat(invoiceAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const PriceAmountCell = ({ workorder }: CellProps) => {
    const priceAmount = workorder?.priceNumeric;

    const formattedAmount =
      priceAmount !== undefined && priceAmount !== null
        ? `$${parseFloat(priceAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const AccuralAmountCell = ({ workorder }: CellProps) => {
    const accrualAmount = workorder?.accrualAmountNumeric;

    const formattedAmount =
      accrualAmount !== undefined && accrualAmount !== null
        ? `$${parseFloat(accrualAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const JobsiteCell = ({ workorder }: CellProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const handleClickJobsite = () => {
      // navigate(`/units?searchType=jobsites&searchText=${workorder?.jobsite}`);
      window.open(`/units/profile/${workorder?.unitID}`, "_blank");
    };
    return (
      <td style={{ cursor: 'pointer' }} onClick={handleClickJobsite}>
        <Typography
          component="span"
          style={{
            color: isHovered ? '#0000b5' : '#0000b5',
            textDecoration: isHovered ? 'underline' : 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {workorder?.jobsite}
        </Typography>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'workOrderNumber') {
      return (dataItem: any) => {
        return <WorkOrderCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'invoiceAmountNumeric') {
      return (dataItem: any) => {
        return <InvoiceAmountCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'accrualAmountNumeric') {
      return (dataItem: any) => {
        return <AccuralAmountCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'priceNumeric') {
      return (dataItem: any) => {
        return <PriceAmountCell workorder={dataItem.dataItem} />;
      };
    }
    if (field === 'jobsite') {
      // Return a function that takes a data item and returns a JSX element
      return (dataItem: any) => {
        return <JobsiteCell workorder={dataItem.dataItem} />;
      };
    }
    return undefined;
  };

  const rowRender = (row: React.ReactElement, rowProps: GridRowProps) => {
    const trElement = React.cloneElement(row, {
      style: { cursor: 'pointer' },
      onClick: () => handleRowClick(rowProps.dataItem as any),
    });
    return trElement;
  };

  const closeDialogReason = () => { setIsOpenReason(false); }

  const closeReadyConfirmDialog = () => { setConfirmReadyDialog(false); }

  const handleConfrimReady = () => {
    closeReadyConfirmDialog();
    setOpenReadyDialog(true);
  }

  const handleRowClick = (dataItem: any) => {
    schedulingRedirect(dataItem?.workOrderID);
  };

  const handleSaveComplete = () => {
    setOpenSchedule(false);
    refetch();
  };

  const handleScheduleDate = (isPrerequest: boolean, date: any) => {
    if (isPrerequest) updateStatus(date);
    else {
      setScheduledDate(date);
      setIsOpen(true);
    }
  };

  const handleReadyDialogClosed = () => {
    setOpenReadyDialog(false);
    setScheduledDate('');
  };

  const closeDialog = () => {
    setIsOpen(false);
    if (status !== 'Ready') {
      setStatus('');
    }
  };

  const handleDeclinedWorkOrder = (workOrderData: any) => {
    setIsOpenReason(true);
    // setIsDeclinedOpen(true);
    setWorkOrderData(workOrderData);
  }

  const handleWorkOrderStatus = (workOrderData: any) => {
    if (workOrderData?.actionType == 'Ready') {
      setOpenReadyDialog(true);
    }
    if (workOrderData?.actionType == 'Forecast' || workOrderData?.actionType == 'UnSchedule') setIsOpen(true);
    if (workOrderData?.actionType == 'Reschedule') setOpenSchedule(true);
    setWorkOrderData(workOrderData);
  }

  const redirectBulkScheduleElement = (dataItem: any) => {
    const actionType = dataItem?.dataItem?.actionType;
    return (
      <Grid container spacing={0.2}>
        {
          dataItem?.dataItem?.showDeclineButton && (
            <Grid item lg={6} sm={6} xl={6} xs={6}>
              <Button size='small' variant='contained'
                style={styling.actionButtonStyle}
                onClick={() => handleDeclinedWorkOrder(dataItem?.dataItem)}>
                DECLINE
              </Button>
            </Grid>
          )
        }
        {
          actionType && (
            <Grid item lg={6} sm={6} xl={6} xs={6}>
              <Button size='small' variant='contained'
                style={styling.actionButtonStyle}
                onClick={() => handleWorkOrderStatus(dataItem?.dataItem)}>
                {actionType}
              </Button>
            </Grid>
          )
        }
      </Grid>
    );
  }

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout
  } = useLoadData<ApiResponse<GridPreference>>(
    gridApiKeyStore.getGridLayout('DuxInspectionRequest', 'DuxInspectionRequest')
  );

  const { mutateAsync: mutateDuxInspectionAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredDuxInspectionRequests, setFilteredDuxInspectionRequests] = useState<DuxInspectionRequest[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredDuxInspectionRequestList = data?.data?.filter((DuxInspection) =>
      Object.values(DuxInspection).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredDuxInspectionRequests(filteredDuxInspectionRequestList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'DuxInspectionRequest',
      pageName: 'DuxInspectionRequest',
    };

    mutateDuxInspectionAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'DuxInspectionRequest',
      pageName: 'DuxInspectionRequest',
    };

    mutateDuxInspectionAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  return (
    <SiTrackPage title="DUX Inspection Request">
      <ReactHookForm formName="DUX_INSPECTION_REQUEST_FORM" onSubmit={handleSubmit} formContextProps={form}>
        <Grid container spacing={1}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <DUXInspectionFilters onShowAllWorkOrder={handleShowAll} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Paper elevation={0} style={styling.paperStyle}>
              <SiTrackGrid
                fileTitle='dux_inspection'
                columns={duxInspectionRequestColumns}
                dataItemKey="workOrderID"
                customCell={(field) =>
                  CustomGridCell({
                    field,
                  })
                }
                actionPermissions={{
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
                optionWidth='250px'
                isBusy={isLoading || isFetching || isLoadingGridLayout}
                CustomComponent={redirectBulkScheduleElement}
                // handleRowClick={(data) => schedulingRedirect(data?.dataItem?.workOrderID)}
                // rowRender={rowRender}

                // -----------------Grid Layout-------
                dataItems={filteredDuxInspectionRequests || []}
                reorderable
                handleSearch={handleInputChange}
                handleSaveLayout={handleSaveLayout}
                isSaveLayout={true}
                gridConfigFromApi={gridConfig}
                handleResetLayout={handleResetLayout}
              // -----------------Grid Layout-------
              />
            </Paper>
          </Grid>
        </Grid>
      </ReactHookForm>
      {isOpen && (
        <ConfirmDialogComponent
          isLoading={isStatusLoading}
          isParentLoading={isLoading}
          message={'Are you sure you want to change the status?'}
          onSubmit={handleStatusClick}
          onClosed={closeDialog}
        />
      )}
      {isDeclinedOpen && (
        <ConfirmDialogComponent
          isLoading={isDeclinedLoading}
          isParentLoading={isLoading}
          message={'Are you sure you want to decline?'}
          onSubmit={handleWorkOrderDecline}
          onClosed={() => setIsDeclinedOpen(false)}
        />
      )}
      {isOpenReadyDialog && (
        <ReadyDialogComponent
          workorderID={workOrderData?.workOrderID}
          onScheduleDate={handleScheduleDate}
          onClosed={handleReadyDialogClosed}
          // inspectionRequestOn={workOrderData?.inspectionRequestOn}
          isParentLoading={isStatusLoading}
          inspectionRequestOn={(workOrderData?.inspectionRequestType === 'InspectionRequest' &&
            workOrderData?.workOrderStatus === 'Forecast') ? workOrderData?.proposedScheduledDate : ""}
        />
      )}
      {openSchedule && (
        <ScheduleDialog
          workOrder={workOrderData}
          onClose={() => setOpenSchedule(false)}
          onSaveCompleted={handleSaveComplete} />
      )}
      {isOpenReason && (
        <ReasonDialogComponent
          isOpenReason={isOpenReason}
          isLoading={isDeclinedLoading}
          onSubmit={handleSaveReasonClick}
          onClosed={closeDialogReason}
        />
      )}

      {isConfirmReadyDialog && (
        <ReadyConfirmationDialog
          onSubmit={handleConfrimReady}
          onClosed={closeReadyConfirmDialog}
        />
      )}
    </SiTrackPage>
  );
};
